import { FormikHelpers } from 'formik';
import { MultiValue, SingleValue } from 'chakra-react-select';
import { SelectProps, Select, SelectOption } from 'components/UI/Select';

export type FormSelectProps = SelectProps &
  Pick<FormikHelpers<unknown>, 'setFieldTouched' | 'setFieldValue'> & {
    onChange?: (value: string | string[] | null) => void;
    name: string;
  };

const FormSelect = ({
  setFieldValue,
  setFieldTouched,
  onChange,
  errorProps = {},
  filterConfig = {},
  ...props
}: FormSelectProps) => {
  const { options, value, isMulti, name } = props;

  const findExistingOption = (currentValue: string) =>
    (options as SelectOption[]).find((option: SelectOption) => option.value === currentValue);

  const getValue = () => {
    if (!value || value === '') return null;
    if (isMulti && Array.isArray(value)) {
      return value.reduce((acc: SelectOption[], previousOption: string) => {
        const existingOption = findExistingOption(previousOption);

        if (!existingOption) return acc;

        return [
          ...acc,
          {
            label: existingOption.label,
            value: existingOption.value,
          },
        ];
      }, []);
    }

    if (typeof value !== 'string') return null;

    const currentOption = findExistingOption(value);

    return {
      value,
      label: currentOption?.label ?? null,
    };
  };

  const handleChange = (newValue: MultiValue<string | SelectOption> | SingleValue<string | SelectOption>) => {
    const selectValue =
      isMulti && newValue && Array.isArray(newValue)
        ? newValue.map((selectedOption) => selectedOption.value)
        : (newValue as SelectOption)?.value;

    setFieldValue(name, selectValue);
    if (onChange) onChange(selectValue);
  };

  return (
    <Select
      {...props}
      filterConfig={filterConfig}
      errorProps={errorProps}
      value={getValue()}
      onChange={handleChange}
      onBlur={() => {
        setFieldTouched(name, true);
      }}
    />
  );
};

export { FormSelect };
